import { useLocation } from "react-router-dom";
import { ICONS } from "../constants/icons";
import useMoveTo from "../constants/navigation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import { ROUTES } from "../App";
import { useUser } from "../contexts/UserContext";
import useUnreadMessages from "../hooks/useUnreadMessages";
import useUnreadActivityCount from "../hooks/useUnreadActivityCount";
import { BsBriefcase } from "react-icons/bs";

const MobileBottomNav = () => {
  const { currentUser } = useUser();
  const moveTo = useMoveTo();
  const location = useLocation();
  const isKeyboardOpen = useKeyboardOpen();
  const unreadCount = useUnreadMessages();
  const activityUnreadCount = useUnreadActivityCount();
  const navRef = useRef(null);

  const navItems = [
    {
      label: "Home",
      icon: ICONS.home,
      path: ROUTES.DASHBOARD,
      exactActivePaths: [],
      activePaths: [
        ROUTES.DASHBOARD,
        ROUTES.COMMUNITY_SEARCH,
        ROUTES.COMMUNITY_CHAT,
      ],
    },
    {
      label: "Studio",
      icon: ICONS.studio,
      path: ROUTES.STUDIO,
      exactActivePaths: [],
      activePaths: [
        ROUTES.STUDIO,
        ROUTES.MY_COMMUNITY,
        ROUTES.SUBSCRIPTION_PLAN,
        ROUTES.NEW_COMMUNITY,
        ROUTES.MY_PRODUCTS,
        ROUTES.ADD_PRODUCT,
        ROUTES.ADD_COURSE,
        ROUTES.ADD_CHAPTER,
        ROUTES.MANAGE_CHAPTER,
        ROUTES.CHAPTER_CONTENT,
        ROUTES.ADD_LESSON,
      ],
    },
    {
      label: "Library",
      icon: ICONS.store,
      path: ROUTES.STORE,
      exactActivePaths: [],
      activePaths: [
        ROUTES.STORE,
        ROUTES.MY_LIBRARY,
        ROUTES.STORE_COURSES,
        ROUTES.PRODUCT_DETAILS,
        ROUTES.CHAPTERS,
        ROUTES.LESSONS_IN_CHAPTER,
        ROUTES.VIEW_LESSON,
      ],
    },
    {
      label: "Chat",
      icon: ICONS.chat,
      path: ROUTES.CHATS,
      exactActivePaths: [],
      activePaths: [
        ROUTES.CHATS,
        ROUTES.PRIVATE_USER_CHAT,
        ROUTES.CREATE_GROUP,
        ROUTES.GROUP_CHAT,
      ],
    },
    {
      label: "Jobs",
      icon: ICONS.jobs,
      ReactIcon: BsBriefcase,
      path: ROUTES.JOBS_FIND,
      exactActivePaths: [ROUTES.JOBS],
      activePaths: [
        ROUTES.JOBS_FIND,
        ROUTES.JOBS_POST,
        ROUTES.JOBS_APPLY,
        ROUTES.JOBS_MY_POSTS,
        ROUTES.JOBS_APPLICANTS,
      ],
    },
    {
      label: "Profile",
      icon: ICONS.profile,
      path: ROUTES.PROFILE,
      exactActivePaths: [],
      activePaths: [
        ROUTES.PROFILE,
        ROUTES.EDIT_PROFILE,
        ROUTES.SETTINGS,
        ROUTES.DELETE_ACCOUNT,
      ],
    },
  ];

  const isActivePath = (item) =>
    item.exactActivePaths.some((p) => location.pathname === p) ||
    item.activePaths.some((p) => location.pathname.startsWith(p));

  // Derive initial active index from location
  const initialIndex = navItems.findIndex((item) => isActivePath(item));
  const [activeIndex, setActiveIndex] = useState(
    initialIndex === -1 ? 0 : initialIndex,
  );
  const [prevActiveIndex, setPrevActiveIndex] = useState(
    initialIndex === -1 ? 0 : initialIndex,
  );
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // indicator state: left (px) for transform, width (px), visible flag
  const [indicator, setIndicator] = useState({
    x: 0,
    width: 0,
    visible: false,
  });

  // Recompute activeIndex when route changes
  useEffect(() => {
    const idx = navItems.findIndex((item) => isActivePath(item));
    if (idx !== -1 && idx !== activeIndex) {
      setShouldAnimate(false);
      setPrevActiveIndex(activeIndex);
      setActiveIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Measure button position/width
  useLayoutEffect(() => {
    const computeIndicator = () => {
      if (!navRef.current) return;
      const buttons = Array.from(navRef.current.querySelectorAll("button"));
      const btn = buttons[activeIndex];
      if (btn) {
        setIndicator({
          x: btn.offsetLeft,
          width: btn.offsetWidth,
          visible: true,
        });
      }
    };

    computeIndicator();

    window.addEventListener("resize", computeIndicator);
    return () => window.removeEventListener("resize", computeIndicator);
  }, [activeIndex]);

  if (isKeyboardOpen) return null;

  const handleClick = (index, path) => {
    // Store previous index before updating to new one
    setShouldAnimate(true); // only animate on user click
    setPrevActiveIndex(activeIndex);
    setActiveIndex(index);
    window.setTimeout(() => {
      moveTo(path);
    }, 250);
  };

  // Calculate animation direction and starting point
  const getAnimationStyle = () => {
    if (!navRef.current)
      return {
        transform: `translateX(${indicator.x}px)`,
        width: `${indicator.width}px`,
      };

    const buttons = Array.from(navRef.current.querySelectorAll("button"));
    const prevBtn = buttons[prevActiveIndex];
    const currentBtn = buttons[activeIndex];

    if (!prevBtn || !currentBtn) {
      return {
        transform: `translateX(${indicator.x}px)`,
        width: `${indicator.width}px`,
      };
    }

    const currentX = currentBtn.offsetLeft;
    const currentWidth = currentBtn.offsetWidth;

    // If we're animating from previous to current
    if (prevActiveIndex !== activeIndex) {
      return {
        transform: `translateX(${currentX}px)`,
        width: `${currentWidth}px`,
      };
    }

    return {
      transform: `translateX(${indicator.x}px)`,
      width: `${indicator.width}px`,
    };
  };

  const isPreview = location.pathname.includes("/preview/");
  if (isPreview && !currentUser) return null;

  return (
    <div className="mobile-bottom-nav md:hidden pb-1 pt-1 bg-white dark:bg-[#1D1F24] border-t border-gray-200 dark:border-[#1D1F24] relative">
      <div ref={navRef} className="flex justify-around py-2.5 relative">
        {/* Animated indicator */}
        <div
          className={`absolute top-1 bottom-1 bg-[#327691] rounded-lg ${shouldAnimate ? "transition-all duration-300 ease-in-out" : ""}`}
          style={{
            left: 0,
            ...getAnimationStyle(),
            opacity: indicator.visible ? 1 : 0,
            zIndex: 0,
          }}
        />

        {navItems.map((item, i) => {
          const active = i === activeIndex;
          return (
            <button
              key={item.label}
              onClick={() => handleClick(i, item.path)}
              className="relative z-10 flex flex-row items-center p-2 justify-center rounded-lg"
              aria-current={active ? "page" : undefined}
            >
              <div className="relative">
                {item.ReactIcon ? (
                  <item.ReactIcon
                    className={`w-6 h-6 ${
                      active ? "text-white" : "text-[#111827] dark:text-white opacity-80"
                    }`}
                    aria-hidden="true"
                  />
                ) : (
                  <img
                    src={item.icon}
                    className={`w-6 h-6 ${
                      active
                        ? "invert brightness-0 dark:invert"
                        : "opacity-80 brightness-0 dark:invert"
                    }`}
                    alt={item.label}
                  />
                )}

                {/* 🔥 UNREAD BADGE (CHAT) */}
                {item.label === "Chat" && unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}

                {/* 🔥 UNREAD BADGE (ACTIVITY) */}
                {item.label === "Profile" && activityUnreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                    {activityUnreadCount > 99 ? "99+" : activityUnreadCount}
                  </span>
                )}
              </div>
              {active && (
                <span className="text-xs ml-3 text-white">{item.label}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileBottomNav;
